import { put, call, select } from 'redux-saga/effects';
import { updateAuth, initAuth } from 'redux/actions/auth';
import { isEmail } from 'utils/validations';
import * as APIs from 'services/api';
import { initApp, setLoading, updateApp } from 'redux/actions/app';
import { updateApaveProducts } from 'redux/actions/cart';
import { showMessage } from 'utils';

// Helper function to extract lockout time from error message
const extractLockoutInfo = (errorMessage, email) => {
  if (!errorMessage || !errorMessage.toLowerCase().includes('account locked')) {
    return { lockoutEndTime: null, lockoutEmail: '' };
  }
  
  // Extract time from message like "Please try again in 30s" or "Please try again in 30 seconds"
  const timeMatch = errorMessage.match(/(\d+)\s*(?:s|second|seconds)/i);
  if (timeMatch) {
    const seconds = parseInt(timeMatch[1], 10);
    const lockoutEndTime = Date.now() + (seconds * 1000);
    return { lockoutEndTime, lockoutEmail: email || '' };
  }
  
  return { lockoutEndTime: null, lockoutEmail: '' };
};

export function* login(action) {
  const { payload: { email, password, code, token, callback } } = action;
  yield put(
    updateAuth({
      errorMessage: '',
    }),
  );
  if (!token) {
    if (email === '' || password === '') {
      return yield put(
        updateAuth({
          errorMessage: 'Please input email and password',
        }),
      );
    }
    if (!isEmail(email)) {
      return yield put(
        updateAuth({
          errorMessage: 'Please input a valid email',
        }),
      );
    }
  }
  try {
    yield put(setLoading(true));
    const res = yield call(token ? APIs.magicLogin : APIs.signin, token ? { token } : {
      email, password, code
    });
    yield put(setLoading(false));
    if (res.code === 200) {
      const { token } = res;
      if (token) {
        yield put(
          updateAuth({
            ...token,
            loggedIn: true,
            email: String(email).toLowerCase().trim(),
            errorMessage: '',
            lockoutEndTime: null,
            lockoutEmail: '',
          }),
        );
      } else {
        callback?.(true) // eslint-disable-line no-unused-expressions
        return;
      }
    }
    if (res.code === 201) {
      yield put(
        updateAuth({
          errorMessage: '',
          email: action.payload.email,
        }),
      );
      yield put(
        updateApp({
          showVerification: true,
        }),
      );
    } else {
      if(token) callback?.(true) // eslint-disable-line no-unused-expressions
      const errorMessage = (res.code == 400 || res.code == 429) && res.message ? res.message : "L'email et/ou le mot de passe semble erroné(s). Merci de vérifier les informations et essayer à nouveau.";
      const lockoutInfo = res.code == 429 ? extractLockoutInfo(errorMessage, email) : { lockoutEndTime: null, lockoutEmail: '' };
      yield put(
        updateAuth({
          errorMessage,
          ...lockoutInfo,
        }),
      );
    }
  } catch (error) {
    console.log(error)
    yield put(setLoading(false));
    // Handle error responses with code and message (e.g., 429 for account lockout)
    const errorMessage = (error.code == 400 || error.code == 429) && error.message 
      ? error.message 
      : "L'email et/ou le mot de passe semble erroné(s). Merci de vérifier les informations et essayer à nouveau.";
    const lockoutInfo = error.code == 429 ? extractLockoutInfo(errorMessage, email) : { lockoutEndTime: null, lockoutEmail: '' };
    yield put(
      updateAuth({
        errorMessage,
        ...lockoutInfo,
      }),
    );
  }
}

export function* signup(action) {
  try {
    yield put(setLoading(true));
    const res = yield call(APIs.signup, action.payload);
    yield put(setLoading(false));
    if (res.code === 201) {
      yield put(
        updateAuth({
          errorMessage: '',
          email: action.payload.email,
        }),
      );
      yield put(
        updateApp({
          showVerification: true,
        }),
      );
    } else if (res.code === 200) {
      showMessage({
        message: 'Submitted successfully!',
        type: 'success'
      });
      window.location.href = "/auth/login";
    } else {
      showMessage({
        message: res.message,
        type: 'warning'
      });
      yield put(
        updateAuth({
          errorMessage: res.message,
        }),
      );
    }
  } catch (error) {
    console.log(error)
    yield put(setLoading(false));
    yield put(
      updateAuth({
        errorMessage: error.message,
      }),
    );
    showMessage({
      message: error.message,
      type: 'warning'
    });
  }
}

export function* logout() {
  const { email, refreshToken, accessToken } = yield select(state => state.auth);
  yield put(setLoading(true));
  try {
    yield call(APIs.signout, { email, refreshToken, accessToken });
    yield put(updateApaveProducts([]));
    yield put(setLoading(false));
    yield put(initAuth());
    yield put(initApp());
  } catch (error) {
    console.log(error)
    yield put(setLoading(false));
    yield put(initAuth());
    yield put(initApp());
  }
}
